import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Box, DismissablePill, StyleSheet, TextInput, spacing } from '@coursera/coursera-ui';

import { Spacer } from 'bundles/enterprise-ui/components';
import Button from 'bundles/internal-ui/components/buttons/Button';

import _t from 'i18n!nls/internal-enterprise';

const styles = StyleSheet.create({
  Pill: {
    marginBottom: spacing.sm,
    marginRight: spacing.sm,
  },
});

export const linkNewPartner = ({
  thirdPartyOrganizationId,
  partnerAdmins,
  shortName,
  name,
}: {
  thirdPartyOrganizationId: string;
  partnerAdmins: number[];
  shortName?: string;
  name?: string;
}) =>
  fetch(`/api/enterpriseAuthoringMappings.v1?action=linkNewPartner`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      organizationId: thirdPartyOrganizationId,
      partnerAdmins,
      shortName,
      name,
    }),
  });

type PropsFromCaller = {
  thirdPartyOrganizationId: string;
  onPartnerCreated: () => void;
};

type Props = PropsFromCaller;
type State = {
  shortName: string;
  name: string;
  partnerAdmins: number[];
};

const INITIAL_STATE = {
  shortName: '',
  name: '',
  partnerAdmins: [],
};

class PrivateAuthoringNewPartner extends React.Component<Props, State> {
  static contextTypes = {
    addToast: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof PrivateAuthoringNewPartner.contextTypes>;

  state: State = INITIAL_STATE;

  handleSlugChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      shortName: event.target.value,
    });
  };

  handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      name: event.target.value,
    });
  };

  /*
  When a partner is created for an organization, the EducatorRolesBackfillJob
  will be triggered to backfill the educator roles for existing admins in that organization.
  Enterprise organization admins will be granted educator partner admin roles.
  Enterprise program admins will be granted educator partner level instructor roles.
  */
  handleCreatePartner = () => {
    const { addToast } = this.context;
    const { thirdPartyOrganizationId, onPartnerCreated } = this.props;
    const { shortName, name, partnerAdmins } = this.state;
    linkNewPartner({ thirdPartyOrganizationId, partnerAdmins, shortName, name })
      .then((_) => _.json())
      .then((response) => {
        if ('errorCode' in response) {
          return Promise.reject(response);
        }
        addToast(_t('Created!'));
        onPartnerCreated();
        this.setState(INITIAL_STATE);
        return Promise.resolve();
      })
      .catch(() => {
        addToast(_t('Failed!'));
      });
  };

  render() {
    const { shortName, name, partnerAdmins } = this.state;
    return (
      <div>
        <TextInput componentId="slug" label={_t('Slug')} value={shortName} onChange={this.handleSlugChange} />
        <Spacer size={32} />
        <TextInput componentId="name" label={_t('Name')} value={name} onChange={this.handleNameChange} />
        <Spacer size={32} />
        <Box flexWrap="wrap">
          {partnerAdmins.map((userId) => (
            <DismissablePill
              size="lg"
              tag="button"
              label={userId}
              onDismiss={() => {
                this.setState({ partnerAdmins: partnerAdmins.filter((_) => _ !== userId) });
              }}
              rootClassName={styles.Pill}
            />
          ))}
        </Box>
        <Spacer size={12} />
        <Button text={_t('Create')} onClick={this.handleCreatePartner} btnStyle="primary" />
      </div>
    );
  }
}

export default PrivateAuthoringNewPartner;
